import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Plus, Edit2, Trash2, Share2, TestTube, Play, Square, Wifi, WifiOff, ChevronDown, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import ConfirmModal from '../ConfirmModal';

export default function WebSocketManagement() {
  const [webSockets, setWebSockets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWebSocket, setSelectedWebSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteWebSocketId, setDeleteWebSocketId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    protocols: '',
    autoReconnect: true,
    heartbeatInterval: 30000,
    maxReconnectAttempts: 5,
    reconnectInterval: 2000,
    messageHandlers: '',
    isActive: true,
    compression: false,
    bufferSize: 1024
  });

  useEffect(() => {
    loadWebSockets();
  }, []);

  const loadWebSockets = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockWebSockets = [
        {
          id: 1,
          name: 'Real-time Dashboard',
          url: 'ws://localhost:8080/data',
          protocols: 'axis-protocol',
          autoReconnect: true,
          heartbeatInterval: 30000,
          maxReconnectAttempts: 5,
          reconnectInterval: 2000,
          isActive: true,
          connectionStatus: 'connected',
          compression: false,
          bufferSize: 1024,
          messagesReceived: 3247,
          messagesSent: 1523,
          lastMessage: new Date().toLocaleString(),
          bytesTransferred: 456789,
          uptime: '02:45:33',
          reconnectCount: 0,
          messageHandlers: {
            'tagUpdate': 'handleTagUpdate',
            'alarm': 'handleAlarm',
            'command': 'handleCommand'
          }
        },
        {
          id: 2,
          name: 'External System Integration',
          url: 'wss://external-system.com:443/api/ws',
          protocols: 'json-protocol',
          autoReconnect: true,
          heartbeatInterval: 60000,
          maxReconnectAttempts: 3,
          reconnectInterval: 5000,
          isActive: false,
          connectionStatus: 'disconnected',
          compression: true,
          bufferSize: 2048,
          messagesReceived: 0,
          messagesSent: 0,
          lastMessage: 'Never',
          bytesTransferred: 0,
          uptime: '00:00:00',
          reconnectCount: 12,
          messageHandlers: {
            'data': 'handleExternalData',
            'status': 'handleStatusUpdate'
          }
        }
      ];
      setWebSockets(mockWebSockets);
    } catch (error) {
      console.error('Error loading WebSockets:', error);
      toast.error('WebSocket connections yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (webSocket = null) => {
    if (webSocket) {
      setSelectedWebSocket(webSocket);
      setFormData({
        name: webSocket.name,
        url: webSocket.url,
        protocols: webSocket.protocols,
        autoReconnect: webSocket.autoReconnect,
        heartbeatInterval: webSocket.heartbeatInterval,
        maxReconnectAttempts: webSocket.maxReconnectAttempts,
        reconnectInterval: webSocket.reconnectInterval,
        messageHandlers: JSON.stringify(webSocket.messageHandlers, null, 2),
        isActive: webSocket.isActive,
        compression: webSocket.compression,
        bufferSize: webSocket.bufferSize
      });
    } else {
      setSelectedWebSocket(null);
      setFormData({
        name: '',
        url: '',
        protocols: '',
        autoReconnect: true,
        heartbeatInterval: 30000,
        maxReconnectAttempts: 5,
        reconnectInterval: 2000,
        messageHandlers: '{\n  "tagUpdate": "handleTagUpdate",\n  "alarm": "handleAlarm"\n}',
        isActive: true,
        compression: false,
        bufferSize: 1024
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedWebSocket(null);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      // Mock connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      const success = Math.random() > 0.3; // 70% success rate
      
      if (success) {
        toast.success('WebSocket connection test successful');
      } else {
        toast.error('WebSocket connection test failed');
      }
    } catch (error) {
      console.error('Error testing WebSocket connection:', error);
      toast.error('Connection test hatası');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    try {
      let messageHandlers = {};
      try {
        messageHandlers = JSON.parse(formData.messageHandlers);
      } catch (e) {
        toast.error('Message handlers JSON formatı hatalı');
        return;
      }

      if (selectedWebSocket) {
        // Update existing WebSocket
        const updatedWebSocket = { 
          ...selectedWebSocket, 
          ...formData,
          messageHandlers,
          lastMessage: new Date().toLocaleString()
        };
        setWebSockets(webSockets.map(ws => ws.id === selectedWebSocket.id ? updatedWebSocket : ws));
        toast.success('WebSocket başarıyla güncellendi');
      } else {
        // Create new WebSocket
        const newWebSocket = {
          id: Math.max(...webSockets.map(ws => ws.id), 0) + 1,
          ...formData,
          messageHandlers,
          connectionStatus: 'disconnected',
          messagesReceived: 0,
          messagesSent: 0,
          lastMessage: 'Never',
          bytesTransferred: 0,
          uptime: '00:00:00',
          reconnectCount: 0
        };
        setWebSockets([...webSockets, newWebSocket]);
        toast.success('WebSocket başarıyla eklendi');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving WebSocket:', error);
      toast.error('WebSocket kaydedilirken hata oluştu');
    }
  };

  const handleDelete = (webSocketId) => {
    setDeleteWebSocketId(webSocketId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setWebSockets(webSockets.filter(ws => ws.id !== deleteWebSocketId));
      toast.success('WebSocket başarıyla silindi');
    } catch (error) {
      console.error('Error deleting WebSocket:', error);
      toast.error('WebSocket silinirken hata oluştu');
    }
  };

  const handleToggleConnection = async (webSocketId) => {
    try {
      setWebSockets(webSockets.map(ws => 
        ws.id === webSocketId 
          ? { 
              ...ws, 
              isActive: !ws.isActive,
              connectionStatus: !ws.isActive ? 'connecting' : 'disconnected'
            }
          : ws
      ));
      toast.success('WebSocket durumu güncellendi');
    } catch (error) {
      console.error('Error toggling WebSocket:', error);
      toast.error('WebSocket durumu güncellenirken hata oluştu');
    }
  };

  const getStatusChip = (webSocket) => {
    if (!webSocket.isActive) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    
    switch (webSocket.connectionStatus) {
      case 'connected':
        return <Chip label="Connected" size="small" color="success" icon={<Wifi size={14} />} />;
      case 'connecting':
        return <Chip label="Connecting" size="small" color="warning" />;
      case 'disconnected':
      default:
        return <Chip label="Disconnected" size="small" color="error" icon={<WifiOff size={14} />} />;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">WebSocket Connections</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${webSockets.filter(ws => ws.connectionStatus === 'connected').length}/${webSockets.length} Active`} 
                  size="small" 
                  color="success" 
                  variant="outlined"
                  icon={<Zap size={14} />}
                />
                <Button 
                  variant="contained" 
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading}
                >
                  Add WebSocket
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {webSockets.map((webSocket) => (
              <Accordion key={webSocket.id}>
                <AccordionSummary expandIcon={<ChevronDown />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Share2 size={20} />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {webSocket.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {webSocket.url}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusChip(webSocket)}
                      <Chip 
                        label={webSocket.uptime} 
                        size="small" 
                        variant="outlined" 
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Connection Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleConnection(webSocket.id)}
                          color={webSocket.isActive ? "error" : "success"}
                          title={webSocket.isActive ? "Disconnect" : "Connect"}
                        >
                          {webSocket.isActive ? <Square size={16} /> : <Play size={16} />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(webSocket)}
                          color="primary"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(webSocket.id)}
                          color="error"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                      <Typography variant="body2">
                        <strong>Protocol:</strong> {webSocket.protocols}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Heartbeat:</strong> {webSocket.heartbeatInterval}ms
                      </Typography>
                      <Typography variant="body2">
                        <strong>Reconnects:</strong> {webSocket.reconnectCount}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Messages ↓:</strong> {webSocket.messagesReceived}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Messages ↑:</strong> {webSocket.messagesSent}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Data:</strong> {formatBytes(webSocket.bytesTransferred)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Message Handlers:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(webSocket.messageHandlers || {}).map(([type, handler]) => (
                          <Chip 
                            key={type}
                            label={`${type} → ${handler}`} 
                            size="small" 
                            variant="outlined"
                            color="info"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {webSockets.length === 0 && !loading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 300, 
              color: 'text.secondary' 
            }}>
              <Share2 size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="h6">No WebSocket Connections</Typography>
              <Typography variant="body2">Add WebSocket connections for real-time bidirectional communication</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* WebSocket Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedWebSocket ? 'Edit WebSocket Connection' : 'Add WebSocket Connection'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Connection Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="WebSocket URL"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              fullWidth
              required
              placeholder="ws://localhost:8080/data or wss://secure-server.com/ws"
              helperText="Use ws:// for standard or wss:// for secure WebSocket"
            />

            <TextField
              label="Protocols"
              value={formData.protocols}
              onChange={(e) => setFormData({ ...formData, protocols: e.target.value })}
              fullWidth
              placeholder="axis-protocol, json-protocol"
              helperText="Comma-separated list of supported protocols"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Heartbeat Interval (ms)"
                type="number"
                value={formData.heartbeatInterval}
                onChange={(e) => setFormData({ ...formData, heartbeatInterval: parseInt(e.target.value) || 30000 })}
                fullWidth
              />
              <TextField
                label="Reconnect Interval (ms)"
                type="number"
                value={formData.reconnectInterval}
                onChange={(e) => setFormData({ ...formData, reconnectInterval: parseInt(e.target.value) || 2000 })}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Max Reconnect Attempts"
                type="number"
                value={formData.maxReconnectAttempts}
                onChange={(e) => setFormData({ ...formData, maxReconnectAttempts: parseInt(e.target.value) || 5 })}
                fullWidth
              />
              <TextField
                label="Buffer Size (bytes)"
                type="number"
                value={formData.bufferSize}
                onChange={(e) => setFormData({ ...formData, bufferSize: parseInt(e.target.value) || 1024 })}
                fullWidth
              />
            </Box>

            <TextField
              label="Message Handlers (JSON)"
              value={formData.messageHandlers}
              onChange={(e) => setFormData({ ...formData, messageHandlers: e.target.value })}
              fullWidth
              multiline
              rows={4}
              placeholder='{"tagUpdate": "handleTagUpdate", "alarm": "handleAlarm"}'
              helperText="JSON object mapping message types to handler functions"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoReconnect}
                    onChange={(e) => setFormData({ ...formData, autoReconnect: e.target.checked })}
                  />
                }
                label="Auto Reconnect"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.compression}
                    onChange={(e) => setFormData({ ...formData, compression: e.target.checked })}
                  />
                }
                label="Compression"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<TestTube size={16} />}
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.name || !formData.url}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name || !formData.url}
          >
            {selectedWebSocket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete WebSocket Connection"
        content="Are you sure you want to delete this WebSocket connection? This action cannot be undone."
        type="yesNo"
      />
    </Box>
  );
}